/**
 * Modal de création/édition d'un créneau EDT.
 *  - Liste les matières existantes (filtrées par niveau + période + classe)
 *  - Auto-remplit le formateur depuis MatiereEnseignant (sélection si plusieurs)
 *  - Multi-classes en création → tronc commun
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Save, Loader2, Check, BookOpen, User } from 'lucide-react';
import {
  useCreerEdtMutation, useUpdateEdtMutation,
  useMatieresDisponiblesEdtQuery,
} from '../../services/vieScolaireApi';
import { useGetSallesQuery } from '../../services/brassageApi';
import SearchableSelect from '../../components/SearchableSelect';

const JOURS_LABEL: Record<string, string> = {
  lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi',
  jeudi: 'Jeudi', vendredi: 'Vendredi', samedi: 'Samedi',
};

const CRENEAUX_RAPIDE = [
  { label: '08:00 – 10:00', hd: '08:00', hf: '10:00' },
  { label: '10:15 – 12:15', hd: '10:15', hf: '12:15' },
  { label: '13:00 – 15:00', hd: '13:00', hf: '15:00' },
  { label: '15:15 – 17:15', hd: '15:15', hf: '17:15' },
  { label: '17:15 – 19:15 (L3)', hd: '17:15', hf: '19:15' },
];

function niveauDeClasse(code: string): 'L1' | 'L2' | 'L3' | '' {
  const m = code.match(/^L([123])/i);
  return m ? (`L${m[1]}` as any) : '';
}

export default function ModalEdt({
  editing, presetCreneau, classes, classePreselectionnee, niveau, periode,
  onClose, onSaved,
}: {
  editing: any | null;
  presetCreneau?: { jour: string; heure_debut: string; heure_fin: string };
  classes: any[]; // classes filtrées par niveau côté parent
  classePreselectionnee?: string;
  niveau?: 'L1' | 'L2' | 'L3';
  periode?: 'pair' | 'impair';
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!editing;

  const [classesChoisies, setClassesChoisies] = useState<string[]>(() => {
    if (editing) return [editing.classe];
    if (classePreselectionnee) return [classePreselectionnee];
    return [];
  });

  const [matiereId, setMatiereId] = useState<number | null>(null);
  const [libelle, setLibelle] = useState(editing?.libelle_matiere || '');
  const [codeMat, setCodeMat] = useState(editing?.code_matiere || '');
  const [formateurNom, setFormateurNom] = useState(editing?.formateur_nom || '');
  const [formateurId, setFormateurId] = useState<number | null>(editing?.formateur_id ?? null);
  const [salle, setSalle] = useState(editing?.salle || '');
  const [jour, setJour] = useState(editing?.jour || presetCreneau?.jour || 'lundi');
  const [hd, setHd] = useState(editing?.heure_debut || presetCreneau?.heure_debut || '08:00');
  const [hf, setHf] = useState(editing?.heure_fin || presetCreneau?.heure_fin || '10:00');
  const [saisieLibre, setSaisieLibre] = useState(isEdit && !codeMat ? false : isEdit);
  // En édition, on commence en saisie libre (les anciens EDT n'ont pas forcément de matiere_id)

  // Déduire le niveau réel (depuis classePreselectionnee, sinon prop niveau)
  const niveauActuel = useMemo(() => {
    if (niveau) return niveau;
    if (classesChoisies[0]) {
      const n = niveauDeClasse(classesChoisies[0]);
      return n || 'L1';
    }
    return 'L1';
  }, [niveau, classesChoisies]) as 'L1' | 'L2' | 'L3';

  // Période effective (défaut : pair = semestre actuel)
  const periodeActuelle = periode || 'pair';

  // Récupère les matières disponibles (1ère classe sélectionnée pour le filtre option)
  const { data: matieresData, isFetching: matieresLoading } = useMatieresDisponiblesEdtQuery(
    {
      niveau: niveauActuel,
      periode: periodeActuelle,
      classe: classesChoisies[0] || undefined,
    },
    { skip: saisieLibre },
  );
  const matieres = matieresData?.results || [];

  // Liste des salles depuis brassage (sites + salles d'examen)
  const { data: sallesData } = useGetSallesQuery();
  const sallesOptions = useMemo(() => {
    const arr = (sallesData || []) as any[];
    // Format affiché : "Salle 10 — Site Principal" (si plusieurs sites)
    const nbSites = new Set(arr.map(s => s.site)).size;
    return arr
      .filter(s => s.actif !== false)
      .map(s => ({
        value: s.nom,
        label: nbSites > 1 && s.site_nom ? `${s.nom} — ${s.site_nom}` : s.nom,
        capacite: s.capacite,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sallesData]);
  const [salleSaisieLibre, setSalleSaisieLibre] = useState(
    !!salle && !sallesOptions.find(o => o.value === salle),
  );
  // Si on charge la liste et que la salle pré-remplie y figure → bascule en select
  useEffect(() => {
    if (sallesOptions.length > 0 && salle && sallesOptions.find(o => o.value === salle)) {
      setSalleSaisieLibre(false);
    }
  }, [sallesOptions.length, salle]);

  // Si on a un matiere_id et un seul formateur → auto-fill
  const matiereSelectionnee = useMemo(
    () => matieres.find(m => m.id === matiereId),
    [matieres, matiereId],
  );
  useEffect(() => {
    if (!matiereSelectionnee) return;
    setLibelle(matiereSelectionnee.nom_matiere);
    setCodeMat(matiereSelectionnee.code_matiere);
    // Auto-fill formateur si 1 seul, sinon laisser le user choisir
    if (matiereSelectionnee.formateurs.length === 1) {
      setFormateurNom(matiereSelectionnee.formateurs[0].nom);
      setFormateurId(matiereSelectionnee.formateurs[0].id);
    } else if (matiereSelectionnee.formateurs.length === 0) {
      setFormateurNom('');
      setFormateurId(null);
    }
  }, [matiereSelectionnee]);

  const [creer, { isLoading: creating }] = useCreerEdtMutation();
  const [update, { isLoading: updating }] = useUpdateEdtMutation();

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100';

  const toggleClasse = (code: string) => {
    setClassesChoisies(prev =>
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code],
    );
  };

  const handleCreneauRapide = (idx: number) => {
    const c = CRENEAUX_RAPIDE[idx];
    setHd(c.hd); setHf(c.hf);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (classesChoisies.length === 0) { alert('Sélectionnez au moins une classe.'); return; }
    if (!libelle.trim()) { alert('La matière est requise.'); return; }

    try {
      const payload: any = {
        libelle_matiere: libelle,
        code_matiere: codeMat,
        formateur_nom: formateurNom,
        formateur_id: formateurId || null,
        salle,
        jour, heure_debut: hd, heure_fin: hf,
        // Semestre local au niveau : pair → 2, impair → 1
        semestre: periodeActuelle === 'pair' ? 2 : 1,
      };
      if (isEdit) {
        payload.classe = classesChoisies[0];
        await update({ id: editing.id, data: payload }).unwrap();
      } else {
        if (classesChoisies.length > 1) payload.classes = classesChoisies;
        else payload.classe = classesChoisies[0];
        await creer(payload).unwrap();
      }
      onSaved();
    } catch (err: any) {
      alert(err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
         onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-xl max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-white dark:bg-gray-900 z-10 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="font-bold text-gray-900 dark:text-white">
            {isEdit ? 'Modifier le créneau' : 'Nouveau créneau'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {/* Classes */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              {isEdit ? 'Classe' : 'Classe(s)'} <span className="text-red-500">*</span>
              {!isEdit && classesChoisies.length > 1 && (
                <span className="ml-2 text-[10px] font-normal text-purple-600 dark:text-purple-300">
                  → {classesChoisies.length} classes : tronc commun
                </span>
              )}
            </label>
            {isEdit ? (
              <select value={classesChoisies[0] || ''} onChange={(e) => setClassesChoisies([e.target.value])}
                className={inputCls} required>
                {classes.map((c: any) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {classes.map((c: any) => {
                  const actif = classesChoisies.includes(c.code);
                  return (
                    <button key={c.code} type="button" onClick={() => toggleClasse(c.code)}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition inline-flex items-center gap-1 ${
                        actif
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-blue-400'
                      }`}>
                      {actif && <Check size={11} />} {c.code}
                    </button>
                  );
                })}
                {classes.length === 0 && (
                  <span className="text-[11px] text-gray-500">Aucune classe pour ce niveau.</span>
                )}
              </div>
            )}
          </div>

          {/* Matière : sélection ou saisie libre */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 inline-flex items-center gap-1">
                <BookOpen size={11} /> Matière <span className="text-red-500">*</span>
              </label>
              <button type="button" onClick={() => setSaisieLibre(!saisieLibre)}
                className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline">
                {saisieLibre ? 'Choisir dans la liste' : 'Saisie libre'}
              </button>
            </div>

            {!saisieLibre ? (
              <>
                {matieresLoading ? (
                  <div className="text-[11px] text-gray-500 py-2">Chargement des matières {niveauActuel} {periodeActuelle}…</div>
                ) : matieres.length === 0 ? (
                  <div className="text-[11px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                    Aucune matière trouvée pour {niveauActuel} (semestre {periodeActuelle}).
                    Saisissez d'abord les matières dans le module « Gestion Matières », ou utilisez la saisie libre.
                  </div>
                ) : (
                  <SearchableSelect
                    value={matieres.find(m => m.id === matiereId) || null}
                    options={matieres}
                    onChange={(m) => setMatiereId(m ? m.id : null)}
                    getKey={(m) => m.id}
                    getLabel={(m) =>
                      `${m.nom_matiere}${m.option && m.option !== 'Tronc Commun' ? ` (${m.option})` : ''}`
                    }
                    getSubLabel={(m) =>
                      m.formateurs.length > 0
                        ? m.formateurs.map(f => f.nom).join(', ')
                        : ''
                    }
                    getSearchText={(m) =>
                      `${m.nom_matiere} ${m.code_matiere || ''} ${m.option || ''} ${m.formateurs.map(f => f.nom).join(' ')}`
                    }
                    placeholder="— Sélectionner une matière —"
                    required
                    emptyMessage="Aucune matière"
                  />
                )}
              </>
            ) : (
              <input value={libelle} onChange={(e) => setLibelle(e.target.value)}
                className={inputCls} required placeholder="Ex. Algorithmique" maxLength={200} />
            )}
          </div>

          {/* Formateur : auto-fill + choix si plusieurs */}
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 inline-flex items-center gap-1 mb-1.5">
              <User size={11} /> Formateur
            </label>
            {!saisieLibre && matiereSelectionnee && matiereSelectionnee.formateurs.length > 1 ? (
              <select value={formateurId || ''}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  setFormateurId(id);
                  const f = matiereSelectionnee.formateurs.find(x => x.id === id);
                  if (f) setFormateurNom(f.nom);
                }}
                className={inputCls}>
                <option value="">— Choisir —</option>
                {matiereSelectionnee.formateurs.map(f => (
                  <option key={f.id} value={f.id}>{f.nom}</option>
                ))}
              </select>
            ) : !saisieLibre && matiereSelectionnee ? (
              <div className="px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300">
                {formateurNom || <span className="text-gray-400 italic text-xs">Aucun formateur assigné à cette matière</span>}
              </div>
            ) : (
              <input value={formateurNom} onChange={(e) => setFormateurNom(e.target.value)}
                className={inputCls} placeholder="M. Mbemba" maxLength={200} />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Salle</label>
              {sallesOptions.length > 0 && (
                <button type="button" onClick={() => setSalleSaisieLibre(!salleSaisieLibre)}
                  className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline">
                  {salleSaisieLibre ? 'Choisir dans la liste' : 'Saisie libre'}
                </button>
              )}
            </div>
            {!salleSaisieLibre && sallesOptions.length > 0 ? (
              <SearchableSelect
                value={sallesOptions.find(o => o.value === salle) || null}
                options={sallesOptions}
                onChange={(o) => setSalle(o ? o.value : '')}
                getKey={(o) => o.value}
                getLabel={(o) => o.label + (o.capacite ? ` (${o.capacite} places)` : '')}
                getSearchText={(o) => `${o.label} ${o.value} ${o.capacite || ''}`}
                placeholder="— Aucune —"
                clearable
                emptyMessage="Aucune salle"
              />
            ) : (
              <input value={salle} onChange={(e) => setSalle(e.target.value)}
                className={inputCls} placeholder="B12" maxLength={50} />
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Jour</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(JOURS_LABEL).map(([v, l]) => (
                <button key={v} type="button" onClick={() => setJour(v)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border ${
                    jour === v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Créneau horaire</label>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {CRENEAUX_RAPIDE.map((c, idx) => {
                const actif = hd === c.hd && hf === c.hf;
                return (
                  <button key={idx} type="button" onClick={() => handleCreneauRapide(idx)}
                    className={`px-2 py-1.5 text-xs rounded-lg border ${
                      actif
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600'
                    }`}>
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 items-center text-xs">
              <input type="time" value={hd} onChange={(e) => setHd(e.target.value)}
                className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
              <span className="text-gray-500">à</span>
              <input type="time" value={hf} onChange={(e) => setHf(e.target.value)}
                className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100" />
            </div>
          </div>
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg">
            Annuler
          </button>
          <button type="submit" disabled={creating || updating || !libelle || classesChoisies.length === 0}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg inline-flex items-center gap-2">
            {(creating || updating) ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isEdit ? 'Enregistrer' : 'Créer'}
          </button>
        </footer>
      </form>
    </div>
  );
}
